import { ChangeDetectionStrategy, Component } from '@angular/core';

import { TabBarComponent } from './tab-bar.component';
import { currencyCode } from './money';
import { currentLocale } from './locale';

/**
 * A language names itself. The Tools row shows the language the app is set to,
 * and that is written in that language, never translated into another one.
 */
const LANGUAGE_NAMES: Record<string, string> = {
  en: 'English',
  fr: 'Français',
  de: 'Deutsch',
  it: 'Italiano',
  es: 'Español',
};

/**
 * Tools: "Where is everything else?"
 *
 * Every string is translated: the app ships in the same five languages this
 * site does, so a drawn screen has to speak the reader's language too.
 *
 * Structure taken from a 0.3.0 screenshot:
 *
 * - The band carries two buttons, not one. "See Premium" is the solid on-band
 *   pill and "Restore" is the translucent one beside it.
 * - The ground opens with a rule, then two groups: YOUR MONEY and THIS APP.
 * - A row is a title, an optional subtitle, a value on the right, and a
 *   chevron. The value is the point of the row: how many categories there are,
 *   which currency is set, which language is on.
 *
 * The subline is one of the four shipped privacy claims, quoted exactly. It
 * states the rule and names backup as the exception in the same breath. Do not
 * paraphrase it.
 *
 * The hero is drawn in the state most readers will meet first: a fresh
 * install that has not bought anything. The app names the product as the
 * label and says "Not unlocked yet" (`subscriptionNotYet`). It used to draw
 * a trial countdown, from when the app ran seven days of its own; it does
 * not any more.
 */
@Component({
  selector: 'app-screen-tools',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TabBarComponent],
  template: `
    <div class="nq-screen">
      <div class="band">
        <!-- Tools has a plain div here, not a flex kicker row. -->
        <div><span class="kicker">{{ t.title }}</span></div>

        <div class="hero">
          <p class="hero__label">Ledgerly</p>
          <p class="hero__figure hero__figure--tools">{{ t.status }}</p>
          <p class="hero__subline hero__subline--tools">{{ t.subline }}</p>
          <div class="tools-actions">
            <span class="cta cta--solid">{{ t.seePremium }}</span>
            <span class="cta cta--ghost">{{ t.restore }}</span>
          </div>
        </div>
      </div>

      <div class="ground">
        <div class="ground-rule"></div>

        @for (group of groups; track group.title) {
        <p class="section">{{ group.title }}</p>
        @for (item of group.items; track item.name) {
        <div class="row">
          <span class="row__body">
            <span class="row__title">{{ item.name }}</span>
            @if (item.desc) {
            <span class="row__sub">{{ item.desc }}</span>
            }
          </span>
          <span class="row__value">{{ item.value }}</span>
          <span class="chev">›</span>
        </div>
        } }
      </div>

      <app-tab-bar active="tools" />
    </div>
  `,
  styles: [
    `
      /* Tools is 38px and line-height 1.06, not 1.04. */
      .nq-screen .hero__figure--tools {
        font-size: 38px;
        line-height: 1.06;
      }

      .nq-screen .hero__subline--tools {
        max-width: 280px;
      }

      .nq-screen .tools-actions {
        display: flex;
        align-items: center;
        gap: 11.2px;
        margin-top: 16.8px;
      }

      .nq-screen .cta {
        display: inline-block;
        padding: 8.4px 16.8px;
        font-size: 13px;
        border-radius: 999px;
      }

      /* A2: solid on-band pill. Drops saturate() and the inset highlight. */
      .nq-screen .cta--solid {
        background: color-mix(
          in srgb,
          var(--color-neutral-100) 90%,
          transparent
        );
        border: 1px solid
          color-mix(in srgb, var(--color-neutral-100) 60%, transparent);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
        color: var(--color-section);
      }

      /* A1: the default translucent on-band control. */
      .nq-screen .cta--ghost {
        background: color-mix(
          in srgb,
          var(--color-neutral-100) 12%,
          transparent
        );
        backdrop-filter: blur(16px) saturate(150%);
        -webkit-backdrop-filter: blur(16px) saturate(150%);
        border: 1px solid
          color-mix(in srgb, var(--color-neutral-100) 24%, transparent);
        box-shadow:
          inset 0 1px 0
            color-mix(in srgb, var(--color-neutral-100) 20%, transparent),
          0 2px 8px rgba(0, 0, 0, 0.18);
        color: var(--color-neutral-100);
      }

      .nq-screen .row {
        padding: 11.2px 0;
      }
    `,
  ],
})
export class ScreenToolsComponent {
  private readonly locale = currentLocale();

  protected readonly t = {
    title: $localize`:@@tabTools:Tools`,
    status: $localize`:@@frameNotUnlocked:Not unlocked yet`,
    // One of the four shipped privacy claims. It states the rule and names
    // backup as the exception in the same breath. Every translation has to
    // keep that last clause.
    subline: $localize`:@@frameToolsSubline:Ledgerly Pro keeps every figure on this phone. Nothing is sold, and nothing is uploaded unless you switch on backup.`,
    seePremium: $localize`:@@frameSeePrices:See prices`,
    restore: $localize`:@@frameRestore:Restore`,
  };

  /**
   * Two groups, as the app has them. Descriptions are the shipped strings
   * where one exists: "Rent, salary, subscriptions" is better than anything
   * written for a marketing page, because it names the thing rather than the
   * feature.
   *
   * The Language row shows the reader's own language in that language, because
   * that is what the app would be set to. The app is offered in all five
   * languages this site ships in.
   */
  protected readonly groups = [
    {
      title: $localize`:@@frameGroupYourMoney:Your money`,
      items: [
        {
          name: $localize`:@@frameRowCategories:Categories`,
          desc: $localize`:@@frameRowCategoriesDesc:Manage your transaction categories`,
          value: '24',
        },
        {
          name: $localize`:@@frameRowRecurring:Recurring transactions`,
          desc: $localize`:@@frameRowRecurringDesc:Rent, salary, subscriptions`,
          value: '3',
        },
        {
          name: $localize`:@@frameRowCurrency:Currency`,
          desc: '',
          value: currencyCode(this.locale),
        },
      ],
    },
    {
      title: $localize`:@@frameGroupThisApp:This app`,
      items: [
        {
          name: $localize`:@@frameRowLanguage:Language`,
          desc: '',
          value: LANGUAGE_NAMES[this.locale] ?? LANGUAGE_NAMES['en'],
        },
        {
          name: $localize`:@@frameRowTheme:Theme`,
          desc: '',
          value: $localize`:@@frameValueSystem:System`,
        },
      ],
    },
  ];
}
